<template lang="pug">
.inspect
  header.inspect__hero
    .inspect__hero-grid
    .inspect__hero-glow.inspect__hero-glow--blue
    .inspect__hero-glow.inspect__hero-glow--red
    .inspect__hero-inner
      button.inspect__back(@click="doBack")
        FontAwesomeIcon(:icon="['fas', 'arrow-left']")
        span Robôs
      p.inspect__eyebrow
        span.inspect__eyebrow-pip
        | Inspeção de Segurança
      h1.inspect__title Inspeção do Robô
      p.inspect__sub Marque cada item do checklist. A seção só é aprovada com todos os itens resolvidos, e as duas inspeções precisam estar aprovadas para o robô ser elegível à chave. Itens condicionais podem ser marcados como N/A.

  .inspect__wrap
    .inspect__empty(v-if="!getCurrentRobot || !getCurrentRobot.id")
      span.inspect__empty-ic 🤖
      h3.inspect__empty-title Nenhum robô selecionado
      p.inspect__empty-text Volte à lista de robôs e escolha um para inspecionar.
      button.inspect__empty-btn(@click="doBack")
        span Ver robôs
        FontAwesomeIcon(:icon="['fas', 'arrow-right']")

    .inspect__layout(v-else)
      //- ---------- CARTÃO DE IDENTIDADE ----------
      aside.inspect__id
        .inspect__photo
          img.inspect__photo-img(v-if="image" :src="image" alt="")
          FontAwesomeIcon.inspect__photo-ph(v-else :icon="['fas', 'robot']")
          button.inspect__photo-btn(@click="$refs.fileInput.click()")
            FontAwesomeIcon(:icon="['fas', 'camera']")
            | Alterar foto
          input(
            ref="fileInput"
            type="file"
            accept="image/*"
            v-show="false"
            @change="doSelectFile"
          )
        .inspect__id-body
          h2.inspect__id-name {{ robotName }}
          span.inspect__id-cat(v-if="robotCategory") {{ robotCategory }}
          p.inspect__id-team
            FontAwesomeIcon.inspect__id-team-ic(:icon="['fas', 'users']")
            | {{ robotTeam }}
          .inspect__eligible(:class="isEligible ? 'is-apt' : 'is-pending'")
            FontAwesomeIcon(:icon="['fas', isEligible ? 'shield-alt' : 'exclamation-triangle']")
            span {{ isEligible ? 'Apto para a chave' : 'Inspeção pendente' }}

      //- ---------- PAINÉIS DE INSPEÇÃO ----------
      //- A lista de itens vem do backend (é ele que decide quando a fase está
      //- aprovada), e cada clique grava na hora: o inspetor pode reprovar um robô
      //- num item, liberar a bancada e retomar de onde parou quando ele voltar.
      //- Por isso o interruptor aqui é só indicador, não botão.
      .inspect__panels
        p.inspect__loading(v-if="loadingChecklist")
          FontAwesomeIcon(:icon="['fas', 'circle-notch']" spin)
          | Carregando checklist...

        p.inspect__alert(v-else-if="checklistError")
          FontAwesomeIcon(:icon="['fas', 'exclamation-triangle']")
          | Não foi possível carregar o checklist. Recarregue a página.

        template(v-else)
          .inspect__panel(v-for="phase in phases" :key="phase.key" :class="{ 'is-on': phase.ok }")
            .inspect__panel-head
              .inspect__panel-title
                FontAwesomeIcon.inspect__panel-ic(:icon="['fas', phase.icon]")
                span {{ phase.title }}
              .inspect__panel-toggle
                span.inspect__panel-count {{ phase.done }}/{{ phase.items.length }}
                span.inspect__panel-state {{ phase.ok ? 'Aprovada' : 'Pendente' }}
                span.inspect__switch(:class="{ 'is-on': phase.ok }" role="img" :aria-label="phase.title + (phase.ok ? ' aprovada' : ' pendente')")
                  span.inspect__switch-knob
            ul.inspect__checklist
              li.inspect__check-item(v-for="item in phase.items" :key="item.id" :class="itemClass(item.id)")
                button.inspect__check-btn(
                  type="button"
                  :disabled="!!saving[item.id]"
                  :aria-pressed="checks[item.id] === 'ok' ? 'true' : 'false'"
                  @click="doToggle(item, 'ok')"
                )
                  FontAwesomeIcon.inspect__check-ic(v-if="saving[item.id]" :icon="['fas', 'circle-notch']" spin)
                  FontAwesomeIcon.inspect__check-ic(v-else :icon="itemIcon(checks[item.id])")
                  span.inspect__check-text
                    span.inspect__check-label {{ item.label }}
                    span.inspect__check-hint {{ item.hint }}
                button.inspect__check-na(
                  v-if="item.optional"
                  type="button"
                  :disabled="!!saving[item.id]"
                  :class="{ 'is-on': checks[item.id] === 'na' }"
                  :aria-pressed="checks[item.id] === 'na' ? 'true' : 'false'"
                  title="Não se aplica a este robô"
                  @click="doToggle(item, 'na')"
                ) N/A

          p.inspect__alert(v-if="saveError")
            FontAwesomeIcon(:icon="['fas', 'exclamation-triangle']")
            | {{ saveError }}

        .inspect__actions
          span.inspect__autosave(v-if="!loadingChecklist && !checklistError")
            FontAwesomeIcon(:icon="['fas', 'cloud-upload-alt']")
            | O checklist salva sozinho a cada item
          button.inspect__save(@click="doSave")
            FontAwesomeIcon(:icon="['fas', 'save']")
            | Salvar foto
</template>

<script>
import {
  mapGetters,
  mapActions
} from 'vuex'

// Um item conta como resolvido se foi aprovado; os condicionais ("caso o robô
// tenha", robô modular) também contam quando marcados como não aplicáveis.
const isResolved = (item, state) => state === 'ok' || (item.optional && state === 'na')

export default {
  name: 'InspectRobot',
  data: () => ({
    image: '',
    // Checklist (itens + marcações) vem do backend: é ele que decide quando a
    // fase está aprovada, então a lista não é duplicada aqui.
    items: [],
    checks: {},
    saving: {},
    loadingChecklist: true,
    checklistError: false,
    saveError: ''
  }),
  mounted () {
    this.loadChecklist()
    // A imagem não vem mais na lista (/get_robots enxuto): carrega sob demanda.
    this.image = this.getCurrentRobot.image || ''
    if (this.getCurrentRobot.id) {
      this.fetchRobotImage(this.getCurrentRobot.id).then((img) => {
        this.image = img || ''
      })
    }
  },
  computed: {
    ...mapGetters('robots', [
      'getCurrentRobot'
    ]),
    ...mapGetters('categories', [
      'getCategories'
    ]),
    ...mapGetters('events', [
      'getCurrentEventId'
    ]),
    robotName () {
      return (this.getCurrentRobot && this.getCurrentRobot.name) || 'Robô'
    },
    robotTeam () {
      return (this.getCurrentRobot && this.getCurrentRobot.team_name) || '-'
    },
    robotCategory () {
      if (!this.getCurrentRobot || !this.getCategories.length) return ''
      const cat = this.getCategories.find(category => category.id === this.getCurrentRobot.category_id)
      return cat ? cat.name : ''
    },
    // Os dois painéis, montados a partir da lista que o backend devolveu.
    // A aprovação é derivada: só vale com o checklist inteiro resolvido, e o
    // backend recalcula o mesmo na hora de gravar.
    phases () {
      return [
        { key: 'static', title: 'Inspeção Estática', icon: 'clipboard-check' },
        { key: 'dynamic', title: 'Inspeção Dinâmica', icon: 'bolt' }
      ].map((phase) => {
        const items = this.items.filter(item => item.phase === phase.key)
        const done = items.filter(item => isResolved(item, this.checks[item.id])).length
        return { ...phase, items, done, ok: items.length > 0 && done === items.length }
      })
    },
    isEligible () {
      return this.phases.every(phase => phase.ok)
    }
  },
  methods: {
    ...mapActions('robots',
      [
        'setUpdateRobot',
        'setRobotImage',
        'fetchRobotImage',
        'fetchInspectionChecklist',
        'setInspectionCheck'
      ]),
    loadChecklist () {
      this.loadingChecklist = true
      this.checklistError = false
      return this.fetchInspectionChecklist({
        robot_id: this.getCurrentRobot.id,
        event_id: this.getCurrentEventId
      }).then((response) => {
        if (!response || !response.items) {
          this.checklistError = true
          return
        }
        this.items = response.items
        this.checks = { ...(response.checks || {}) }
      }).finally(() => {
        this.loadingChecklist = false
      })
    },
    // Grava na hora: a inspeção pode ser interrompida (robô reprovado volta
    // depois) e o progresso precisa sobreviver a sair da tela. Otimista na UI,
    // com reversão se o servidor recusar.
    doToggle (item, target) {
      if (this.saving[item.id]) return
      const previous = this.checks[item.id] || ''
      const next = previous === target ? '' : target
      this.$set(this.checks, item.id, next)
      this.$set(this.saving, item.id, true)
      this.saveError = ''
      return this.setInspectionCheck({
        robot_id: this.getCurrentRobot.id,
        event_id: this.getCurrentEventId,
        item_id: item.id,
        state: next
      }).then((response) => {
        if (!response || !response.checks) {
          this.$set(this.checks, item.id, previous)
          this.saveError = 'Não foi possível salvar este item. Verifique sua conexão e sua permissão de inspetor.'
          return
        }
        // O servidor é a verdade: ele recalcula a aprovação das duas fases.
        this.checks = { ...response.checks }
      }).finally(() => {
        this.$set(this.saving, item.id, false)
      })
    },
    itemClass (id) {
      return { 'is-on': this.checks[id] === 'ok', 'is-na': this.checks[id] === 'na' }
    },
    itemIcon (state) {
      if (state === 'ok') return ['fas', 'check-circle']
      if (state === 'na') return ['fas', 'minus-circle']
      return ['far', 'circle']
    },
    doBack () {
      this.$router.push('Robots')
    },
    doSelectFile (e) {
      const files = e.target.files || (e.dataTransfer && e.dataTransfer.files)
      if (!files || !files.length) return
      const reader = new FileReader()
      reader.onload = () => { this.image = reader.result }
      reader.readAsDataURL(files[0])
    },
    // Só a foto: a inspeção em si já foi gravada item a item pelo doToggle.
    doSave () {
      const base64 = this.image.split('base64,')[1]
      if (!base64) return
      this.setRobotImage({ robot_id: this.getCurrentRobot.id, image: base64 })
    }
  },
  filters: {},
  watch: {}
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $blue-dark: #0F4FA8
  $red: #DD3434
  $green: #2E9E4F
  $green-dark: #1F7C3C
  $amber: #E65100
  $ink: #0B0E14
  $muted: #5C6673
  $line: rgba(11, 14, 20, 0.10)
  $paper: #FFFFFF
  $page: #F2F4F8
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .inspect
    color: $ink
    background: $page
    min-height: calc(100vh - 64px)

    *
      box-sizing: border-box

    // ---------- HERO ----------
    &__hero
      position: relative
      overflow: hidden
      isolation: isolate
      padding: 26px 0 40px
      background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
      color: #fff

    &__hero-grid
      position: absolute
      inset: 0
      background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      background-size: 42px 42px
      mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      -webkit-mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      z-index: -2

    &__hero-glow
      position: absolute
      width: 360px
      height: 360px
      border-radius: 50%
      filter: blur(110px)
      opacity: 0.5
      z-index: -1
      &--blue
        background: $blue
        top: -170px
        left: -90px
      &--red
        background: $red
        bottom: -190px
        right: -70px

    &__hero-inner
      position: relative
      max-width: 1140px
      margin: 0 auto
      padding: 0 24px
      > *
        animation: insp-rise .55s cubic-bezier(.2, .7, .2, 1) both
      .inspect__title
        animation-delay: .06s
      .inspect__sub
        animation-delay: .12s

    &__back
      display: inline-flex
      align-items: center
      gap: 8px
      background: rgba(255, 255, 255, 0.08)
      border: 1px solid rgba(255, 255, 255, 0.16)
      cursor: pointer
      color: rgba(255, 255, 255, 0.85)
      font-family: $mono
      font-size: 11px
      font-weight: 700
      letter-spacing: 0.08em
      text-transform: uppercase
      padding: 8px 14px
      border-radius: 999px
      margin: 0 0 20px
      transition: background .18s ease, color .18s ease, transform .18s ease
      &:hover
        background: rgba(255, 255, 255, 0.16)
        color: #fff
        transform: translateX(-2px)

    &__eyebrow
      display: inline-flex
      align-items: center
      gap: 10px
      font-family: $mono
      text-transform: uppercase
      letter-spacing: 0.22em
      font-size: 12px
      font-weight: 700
      color: rgba(255, 255, 255, 0.7)
      margin: 0 0 12px

    &__eyebrow-pip
      width: 8px
      height: 8px
      border-radius: 50%
      background: linear-gradient(135deg, #5AA0FF, $red)
      box-shadow: 0 0 0 4px rgba(90, 160, 255, 0.15)

    &__title
      font-family: $display
      font-size: 42px
      line-height: 1.02
      font-weight: 800
      letter-spacing: -0.01em
      margin: 0

    &__sub
      max-width: 620px
      margin: 12px 0 0
      font-size: 15px
      line-height: 1.5
      color: rgba(255, 255, 255, 0.72)

    // ---------- CORPO ----------
    &__wrap
      max-width: 1140px
      margin: 0 auto
      padding: 32px 24px 72px

    &__layout
      display: grid
      grid-template-columns: 340px 1fr
      gap: 24px
      align-items: start
      @media (max-width: 860px)
        grid-template-columns: 1fr

    // ---------- CARTÃO DE IDENTIDADE ----------
    &__id
      background: $paper
      border: 1px solid $line
      border-radius: 18px
      overflow: hidden
      box-shadow: 0 6px 20px rgba(11, 14, 20, 0.06)
      position: sticky
      top: 24px
      @media (max-width: 860px)
        position: static

    &__photo
      position: relative
      height: 260px
      display: flex
      align-items: center
      justify-content: center
      padding: 20px
      background: linear-gradient(160deg, #F6F8FC 0%, #E8EDF5 100%)
      border-bottom: 1px solid $line

    &__photo-img
      max-width: 100%
      max-height: 100%
      object-fit: contain
      filter: drop-shadow(0 8px 16px rgba(11, 14, 20, 0.2))

    &__photo-ph
      font-size: 84px
      color: #C3CBD8

    &__photo-btn
      position: absolute
      right: 12px
      bottom: 12px
      display: inline-flex
      align-items: center
      gap: 7px
      padding: 8px 14px
      border-radius: 999px
      border: 1px solid rgba(11, 14, 20, 0.12)
      background: rgba(255, 255, 255, 0.92)
      backdrop-filter: blur(4px)
      color: $ink
      font-family: $display
      font-size: 13px
      font-weight: 700
      cursor: pointer
      box-shadow: 0 2px 8px rgba(11, 14, 20, 0.12)
      transition: transform .16s ease, box-shadow .16s ease
      &:hover
        transform: translateY(-1px)
        box-shadow: 0 5px 14px rgba(11, 14, 20, 0.18)

    &__id-body
      padding: 18px 20px 22px
      display: flex
      flex-direction: column
      gap: 8px

    &__id-name
      font-family: $display
      font-size: 26px
      font-weight: 800
      line-height: 1.1
      margin: 0
      word-break: break-word

    &__id-cat
      align-self: flex-start
      font-family: $mono
      font-size: 11px
      font-weight: 700
      text-transform: uppercase
      letter-spacing: 0.06em
      color: $blue-dark
      background: rgba(40, 122, 237, 0.10)
      border-radius: 999px
      padding: 4px 12px

    &__id-team
      display: flex
      align-items: center
      gap: 8px
      margin: 2px 0 0
      font-size: 14px
      color: $muted
      &-ic
        font-size: 13px
        color: #AEB7C4

    &__eligible
      display: inline-flex
      align-items: center
      gap: 9px
      margin-top: 8px
      padding: 10px 14px
      border-radius: 12px
      font-family: $display
      font-size: 15px
      font-weight: 700
      border: 1px solid
      &.is-apt
        color: $green-dark
        background: rgba(46, 158, 79, 0.10)
        border-color: rgba(46, 158, 79, 0.4)
      &.is-pending
        color: $amber
        background: rgba(230, 81, 0, 0.08)
        border-color: rgba(230, 81, 0, 0.4)

    // ---------- PAINÉIS ----------
    &__panels
      display: flex
      flex-direction: column
      gap: 20px

    &__loading
      display: flex
      align-items: center
      gap: 10px
      margin: 0
      padding: 22px
      background: $paper
      border: 1px solid $line
      border-radius: 18px
      color: $muted
      font-size: 14px

    &__alert
      display: flex
      align-items: center
      gap: 10px
      margin: 0
      padding: 14px 16px
      border-radius: 12px
      background: rgba(230, 81, 0, 0.08)
      border: 1px solid rgba(230, 81, 0, 0.28)
      color: $amber
      font-size: 14px
      line-height: 1.4

    &__panel
      background: $paper
      border: 1px solid $line
      border-radius: 18px
      padding: 20px 22px
      box-shadow: 0 6px 20px rgba(11, 14, 20, 0.06)
      border-left: 4px solid rgba(11, 14, 20, 0.10)
      transition: border-color .2s ease, box-shadow .2s ease
      &.is-on
        border-left-color: $green
        box-shadow: 0 8px 22px rgba(46, 158, 79, 0.12)

    &__panel-head
      display: flex
      align-items: center
      justify-content: space-between
      gap: 16px
      flex-wrap: wrap

    &__panel-title
      display: flex
      align-items: center
      gap: 11px
      font-family: $display
      font-size: 22px
      font-weight: 700

    &__panel-ic
      font-size: 18px
      color: $blue

    &__panel-toggle
      display: inline-flex
      align-items: center
      gap: 12px

    &__panel-count
      font-family: $mono
      font-size: 11px
      font-weight: 700
      letter-spacing: 0.06em
      color: $muted
      background: rgba(11, 14, 20, 0.05)
      border-radius: 999px
      padding: 4px 10px

    &__panel.is-on &__panel-count
      color: $green-dark
      background: rgba(46, 158, 79, 0.12)

    &__panel-state
      font-family: $mono
      font-size: 11px
      font-weight: 700
      text-transform: uppercase
      letter-spacing: 0.06em
      color: $muted

    &__panel.is-on &__panel-state
      color: $green-dark

    // interruptor (indicador do estado derivado, não é clicável)
    &__switch
      display: block
      position: relative
      width: 52px
      height: 28px
      border-radius: 999px
      border: none
      background: #CBD3DF
      padding: 0
      flex-shrink: 0
      transition: background .22s ease
      &.is-on
        background: linear-gradient(120deg, $green, $green-dark)

    &__switch-knob
      position: absolute
      top: 3px
      left: 3px
      width: 22px
      height: 22px
      border-radius: 50%
      background: #fff
      box-shadow: 0 2px 5px rgba(11, 14, 20, 0.3)
      transition: transform .22s cubic-bezier(.34, 1.56, .64, 1)

    &__switch.is-on &__switch-knob
      transform: translateX(24px)

    // checklist
    &__checklist
      list-style: none
      margin: 16px 0 0
      padding: 14px 0 0
      border-top: 1px dashed $line
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr))
      gap: 8px 16px

    &__check-item
      display: flex
      align-items: flex-start
      gap: 6px
      border-radius: 12px
      border: 1px solid transparent
      transition: background .18s ease, border-color .18s ease
      &.is-on
        background: rgba(46, 158, 79, 0.07)
        border-color: rgba(46, 158, 79, 0.22)
      &.is-na
        background: rgba(11, 14, 20, 0.035)
        border-color: $line

    &__check-btn
      flex: 1
      display: flex
      align-items: flex-start
      gap: 10px
      text-align: left
      background: none
      border: none
      cursor: pointer
      padding: 9px 10px
      border-radius: 12px
      font: inherit
      color: inherit
      &:hover .inspect__check-label
        color: $ink
      &:focus-visible
        outline: 2px solid $blue
        outline-offset: -2px
      &:disabled
        cursor: progress

    &__check-ic
      font-size: 17px
      margin-top: 1px
      flex-shrink: 0
      color: #C3CBD8
      transition: color .18s ease
    &__check-item.is-on &__check-ic
      color: $green
    &__check-item.is-na &__check-ic
      color: #AEB7C4

    &__check-text
      display: flex
      flex-direction: column
      gap: 2px
      min-width: 0

    &__check-label
      font-size: 14px
      font-weight: 600
      line-height: 1.25
      color: $muted
      transition: color .18s ease
    &__check-item.is-on &__check-label
      color: $ink
    &__check-item.is-na &__check-label
      color: #9BA5B3
      text-decoration: line-through

    &__check-hint
      font-size: 12px
      line-height: 1.35
      color: #8F99A8

    &__check-na
      align-self: center
      flex-shrink: 0
      margin-right: 8px
      padding: 4px 8px
      border-radius: 999px
      border: 1px solid $line
      background: none
      cursor: pointer
      font-family: $mono
      font-size: 10px
      font-weight: 700
      letter-spacing: 0.06em
      color: $muted
      transition: background .18s ease, color .18s ease, border-color .18s ease
      &:hover
        border-color: rgba(11, 14, 20, 0.28)
        color: $ink
      &.is-on
        background: $muted
        border-color: $muted
        color: #fff

    // ---------- AÇÃO ----------
    &__actions
      display: flex
      align-items: center
      justify-content: space-between
      gap: 14px
      flex-wrap: wrap
      margin-top: 4px

    &__autosave
      display: inline-flex
      align-items: center
      gap: 8px
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.04em
      color: $muted

    &__save
      display: inline-flex
      align-items: center
      gap: 10px
      padding: 0 34px
      height: 52px
      border: none
      border-radius: 12px
      background: linear-gradient(140deg, $blue, $blue-dark)
      color: #fff
      font-family: $display
      font-size: 18px
      font-weight: 700
      letter-spacing: 0.02em
      cursor: pointer
      box-shadow: 0 8px 20px rgba(40, 122, 237, 0.32)
      transition: transform .16s ease, box-shadow .16s ease, filter .16s ease
      &:hover
        transform: translateY(-1px)
        filter: brightness(1.05)
        box-shadow: 0 10px 26px rgba(40, 122, 237, 0.4)

    // ---------- ESTADO VAZIO ----------
    &__empty
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      text-align: center
      padding: 72px 24px
      background: $paper
      border: 1px dashed rgba(11, 14, 20, 0.16)
      border-radius: 16px

    &__empty-ic
      font-size: 46px
      margin-bottom: 12px

    &__empty-title
      font-family: $display
      font-size: 22px
      font-weight: 700
      margin: 0 0 6px

    &__empty-text
      font-size: 14px
      color: $muted
      margin: 0 0 18px

    &__empty-btn
      display: inline-flex
      align-items: center
      gap: 8px
      padding: 11px 22px
      border: none
      border-radius: 10px
      background: linear-gradient(140deg, $blue, $blue-dark)
      color: #fff
      font-family: $display
      font-size: 15px
      font-weight: 700
      cursor: pointer
      box-shadow: 0 6px 16px rgba(40, 122, 237, 0.28)
      transition: transform .16s ease
      &:hover
        transform: translateY(-1px)

  @keyframes insp-rise
    from
      opacity: 0
      transform: translateY(14px)
    to
      opacity: 1
      transform: translateY(0)
</style>
